import { FlexProps, useDisclosure } from '@chakra-ui/react';
import { captureException } from '@sentry/node';
import { useState } from 'react';
import { useSnackbar } from 'ui-components';
import clientApi from '~/clientApi';
import { SharePageModal } from '~/components/SharePageModal';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';

import { NavBarProps } from '../../MediaPackNavBar.model';
import { ShareNavBar } from './ShareNavBar';

interface Props extends NavBarProps, FlexProps {}

export const ShareNavBarContainer: React.FC<Props> = ({
  mediaPack,
  sectionsRefs,
  isSharedView,
  ...props
}) => {
  const {
    isOpen: isShareModalOpen,
    onClose: onCloseShareModal,
    onOpen: onOpenShareModal,
  } = useDisclosure();
  const [updatedMediaPack, setUpdatedMediaPack] = useState(mediaPack);
  const snackbar = useSnackbar();
  const shareModalText =
    'Share this media pack for the brand by email or copy the link:';

  const handleShareSubmit = async (email: string) => {
    await clientApi.mediaPacks.share({
      id: mediaPack.id,
      email,
      _impersonateOnce: mediaPack?.creator?.id,
    });

    onCloseShareModal();
  };

  const handlePasswordSubmit = async (sharePassword: string) => {
    try {
      await clientApi.mediaPacks.update({
        id: mediaPack.id,
        sharePassword,
        _impersonateOnce: mediaPack?.creator?.id,
      });

      setUpdatedMediaPack({
        ...mediaPack,
        isPasswordProtected: true,
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  const handlePasswordRemove = async () => {
    try {
      await clientApi.mediaPacks.removePassword({
        id: mediaPack.id,
        _impersonateOnce: mediaPack?.creator?.id,
      });

      setUpdatedMediaPack({
        ...mediaPack,
        isPasswordProtected: false,
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  return (
    <>
      <SharePageModal
        onClose={onCloseShareModal}
        isOpen={isShareModalOpen}
        onSubmit={handleShareSubmit}
        onPasswordSubmit={handlePasswordSubmit}
        onRemove={handlePasswordRemove}
        shareableUrl={updatedMediaPack?.shareLink}
        hasPassword={updatedMediaPack?.isPasswordProtected}
        descriptionText={shareModalText}
        modalTitle="Share this Media Pack"
      />

      <ShareNavBar
        mediaPack={updatedMediaPack}
        onShare={onOpenShareModal}
        sectionsRefs={sectionsRefs}
        isSharedView={isSharedView}
        {...props}
      />
    </>
  );
};
